import { Selection } from '@tiptap/pm/state'
import { EditorContent, useEditor } from '@tiptap/react'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
  KNOWN_PLACEHOLDER_TOKENS,
  PLACEHOLDERS
} from '../../constants/mail-template-placeholders'
import { extensions } from '../../constants/tiptap'
import { PLACEHOLDER_STATUS } from '../../helpers/mail-template-placeholders'
import {
  PlaceholderTokens,
  setPlaceholderConfig
} from './extensions/placeholder-tokens'
import MenuBar from './menu-bar'
import './styles.scss'

const I18N = 'mail_template.edit_mail_template.'
const PLACEHOLDER_KEYS = Object.fromEntries(
  Object.values(PLACEHOLDERS).map(({ token, key }) => [token, key])
)

const Tiptap = ({
  initialContent,
  placeholders,
  labelledBy,
  describedBy,
  onCreate,
  onUpdate
}) => {
  const { t } = useTranslation()

  // Die Liste kommt bei jedem Render neu, der Schlüssel hält Effekte stabil
  const allowedKey = (placeholders || []).map(({ macro }) => macro).join('|')
  const allowed = useMemo(
    () => (allowedKey ? allowedKey.split('|') : []),
    [allowedKey]
  )

  // title am Chip, gültige zeigen ihren Namen, ungültige den Fehler
  const describe = useCallback(
    (token, status) =>
      status === PLACEHOLDER_STATUS.VALID
        ? t(`${I18N}placeholder.${PLACEHOLDER_KEYS[token]}.label`)
        : t(`${I18N}placeholder_chip.${status}`, { token }),
    [t]
  )

  const editor = useEditor({
    extensions: [
      ...extensions,
      PlaceholderTokens.configure({
        known: KNOWN_PLACEHOLDER_TOKENS,
        allowed,
        describe
      })
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        ...(labelledBy ? { 'aria-labelledby': labelledBy } : {}),
        ...(describedBy ? { 'aria-describedby': describedBy } : {}),
        'aria-multiline': 'true',
        // min-h macht die ganze Fläche klickbar
        class:
          'min-h-[14rem] px-4 py-3 text-sm text-gray-900 focus:outline-none'
      }
    },
    onUpdate,
    onCreate
  })

  useEffect(() => {
    setPlaceholderConfig(editor, { allowed, describe })
  }, [editor, allowed, describe])

  // Ohne vorherigen Klick in den Editor steht die Auswahl am Dokumentanfang.
  // Ein Platzhalter landet dann am Ende statt vor der Anrede.
  const hasFocusedRef = useRef(false)
  useEffect(() => {
    if (!editor) return undefined
    const onFocus = () => {
      hasFocusedRef.current = true
    }
    editor.on('focus', onFocus)
    return () => editor.off('focus', onFocus)
  }, [editor])

  const getInsertRange = useCallback(() => {
    if (!editor) return null
    const selection = hasFocusedRef.current
      ? editor.state.selection
      : Selection.atEnd(editor.state.doc)
    return { from: selection.from, to: selection.to }
  }, [editor])

  return (
    <div className='rounded-xl border border-gray-200 bg-white focus-within:border-blue-600 focus-within:ring-1 focus-within:ring-blue-600'>
      <MenuBar
        editor={editor}
        placeholders={placeholders}
        getInsertRange={getInsertRange}
      />
      <div className='max-h-[28rem] overflow-y-auto rounded-b-xl'>
        <EditorContent editor={editor} />
      </div>
    </div>
  )
}

Tiptap.propTypes = {
  initialContent: PropTypes.object,
  placeholders: PropTypes.array,
  labelledBy: PropTypes.string,
  describedBy: PropTypes.string,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func
}

Tiptap.defaultProps = {
  initialContent: null,
  placeholders: null,
  labelledBy: undefined,
  describedBy: undefined,
  onCreate: () => undefined,
  onUpdate: () => undefined
}

export default Tiptap
